import React, { useState } from 'react';
import { dig } from '@this/src/util';
import { Button } from '@this/shared/ui/inputs/button';
import { Link as SharedLink } from '@this/shared/ui/navigations/link';
import { Modal, ModalHeader, ModalBody } from '@this/shared/ui/feedbacks/modal';
import AlertArea from '@this/components/reserve_trip/select/alert_area/alert_area';
import type SearchQueryItem from '../../../../domain/search_query_item';
import type { Airline } from '../../../../domain/select_store';
import type User from '../../../../domain/user/user';

import { useSelectContext } from '../context/SelectContext';

import SelectDefaultArea from '../default_area/default_area';
import ListAreaShortDistance from '../list_area/short_distance/ListAreaShortDistance';
import SelectSearchArea from '../search_area/search_area';
import OutlineWrapperShortDistanceContainer from '../outline_wrapper/OutlineWrapperShortDistanceContainer';
import TripPlanSelector from '../outline_wrapper/short_distance/TripPlanSelector';
import ArrangementRequestForm from '../arrangement_request_form/arrangement_request_form';

import type SearchQuery from '../../../../domain/search_query';

interface Props {
  // Props not in Context (remaining)
  showArrangementRequestForm: boolean;
  showSearchBox: boolean;
  additionalSearchErrors?: { [k in string]: string };
  serviceId: number;
  user: User | null;
  availableOptions: string[];

  // Handler props not in Context
  handleAirline: (airline: Airline) => () => void;
  handleSelectItem: () => void;
  handleAddItem: (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  handleRemoveItem: () => void;
  handleReSearch: (query: SearchQuery) => void;
  handleToggleArrangementRequestForm: () => void;
  toggleShowSearchBox: () => void;
  handleOpenShowSearchBox: () => void;
}

const PcShortDistance: React.FC<Props> = ({
  showArrangementRequestForm,
  serviceId,
  user,
  availableOptions,
  showSearchBox,
  additionalSearchErrors,

  handleAirline,
  handleSelectItem,
  handleAddItem,
  handleAdditionalSearch,
  handleRemoveItem,
  handleReSearch,
  handleToggleArrangementRequestForm,
  toggleShowSearchBox,
  handleOpenShowSearchBox
}) => {
  // Get values from Context
  const {
    store,
    repository,
    query,
    selectErrors,
    submitting,
    isAnyItemLoading,
    handleSelectType,
    handleNextButtonClick
  } = useSelectContext();
  const isButtonDisabled = submitting || isAnyItemLoading;
  const [showTooltip, setShowTooltip] = useState(false);

  if (store == null || repository == null) {
    return null;
  }

  return (
    <div className="select-pc" style={getStyles().selectPc}>
      <div style={getStyles().outerContainer}>
        <div style={getStyles().innerContainer}>
          <div style={getStyles().contentContainer}>
            <SelectSearchArea
              query={query}
              user={user}
              trip={store.result.getReservingTrip()}
              selectErrors={selectErrors}
              submitting={isButtonDisabled}
              store={store}
              handleNextButtonClick={handleNextButtonClick}
              handleReSearch={handleReSearch}
              availableOptions={availableOptions}
            />
            <AlertArea result={store.result} />
            <div style={getStyles().mainContent}>
              <div style={getStyles().leftColumn}>
                <TripPlanSelector
                  store={store}
                  serviceId={serviceId}
                  handleSelectType={handleSelectType}
                  handleAirline={handleAirline}
                  width={100}
                />
                <OutlineWrapperShortDistanceContainer
                  query={query}
                  store={store}
                  selectErrors={selectErrors}
                  packageErrors={store.packageErrors[store.result.airline]}
                  handleSelectItem={handleSelectItem}
                  handleAddItem={handleAddItem}
                  handleRemoveItem={handleRemoveItem}
                  handleAdditionalSearch={handleAdditionalSearch}
                  additionalSearchErrors={additionalSearchErrors}
                  handleNextButtonClick={handleNextButtonClick}
                  handleOpenShowSearchBox={handleOpenShowSearchBox}
                  submitting={isButtonDisabled}
                />
                {dig(store, 'user', 'organization', 'arrangement_request_enable?') ? (
                  <div>
                    <Button
                      onClick={handleNextButtonClick}
                      disabled={isButtonDisabled}
                      loading={isButtonDisabled}
                      style={getStyles().reserveButton}
                    >
                      予約確認画面へ
                      <div style={getStyles().reserveButtonArrow} />
                    </Button>
                    <div style={getStyles().requestFormContainer}>
                      <p>
                        お探しの結果が見つからない場合は
                        <SharedLink
                          onClick={handleToggleArrangementRequestForm}
                          style={getStyles().requestFormLink}
                        >
                          リクエストフォーム
                        </SharedLink>
                        からご依頼ください
                      </p>
                      <div style={getStyles().tooltipContainer}>
                        <div
                          style={getStyles().helpButton}
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                          onFocus={() => setShowTooltip(true)}
                          onBlur={() => setShowTooltip(false)}
                          onTouchEnd={() => setShowTooltip(!showTooltip)}
                          tabIndex={0}
                          role="button"
                          aria-label="ヘルプ情報を表示"
                        >
                          ?
                        </div>
                        {showTooltip && (
                          <div style={getStyles().tooltip}>
                            <p>
                              ご希望の検索結果が出てこない場合は、こちらのフォームからご依頼ください
                              <br />
                              <span data-wovn-ignore>{`（手数料: ${dig(
                                store,
                                'user',
                                'organization',
                                'arrangement_request_setting',
                                'fee'
                              )}）`}</span>
                              円
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div style={getStyles().rightColumn}>
                <SelectDefaultArea
                  isOrganization
                  show={store.currentTab === 'none'}
                  serviceId={serviceId}
                  sp={false}
                />
                {store.result.items.map((item, i) => (
                  <ListAreaShortDistance
                    key={item.index}
                    hotelPriceLimit={item.hotelPriceLimit}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'separate' && store.currentTab === item.index}
                    direction={i === 0 ? 'out' : i === 2 ? 'home' : undefined}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                  />
                ))}
                {store.result.packageItems[store.result.airline].map((item, i) => (
                  <ListAreaShortDistance
                    key={item.index}
                    hotelPriceLimit={item.hotelPriceLimit}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'airPackage' && store.currentTab === item.index}
                    direction={i === 0 ? 'out' : i === 2 ? 'home' : undefined}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                  />
                ))}
                {store.result.jrPackageItems.map((item, i) => (
                  <ListAreaShortDistance
                    key={item.index}
                    hotelPriceLimit={item.hotelPriceLimit}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'jrPackage' && store.currentTab === item.index}
                    direction={i === 0 ? 'out' : i === 2 ? 'home' : undefined}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        {showArrangementRequestForm && (
          <Modal open={showArrangementRequestForm} onClose={handleToggleArrangementRequestForm} size="large">
            <ModalHeader>リクエストフォーム</ModalHeader>
            <ModalBody>
              <ArrangementRequestForm
                serviceId={serviceId}
                query={query}
                store={store}
                selectErrors={selectErrors}
                handleSelectItem={handleSelectItem}
                handleAdditionalSearch={handleAdditionalSearch}
                additionalSearchErrors={additionalSearchErrors}
                handleNextButtonClick={handleNextButtonClick}
                handleOpenShowSearchBox={handleOpenShowSearchBox}
                reserveRequestMailNotificationType={
                  store.user?.organization?.reserve_request_mail_notification_type ?? 'togglable'
                }
                shortdistance
              />
            </ModalBody>
          </Modal>
        )}
      </div>
    </div>
  );
};

function getStyles() {
  return {
    selectPc: {
      display: 'flex'
    } as React.CSSProperties,
    outerContainer: {
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      width: '100%',
      minHeight: '0%'
    } as React.CSSProperties,
    innerContainer: {
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      width: '1150px',
      minHeight: '0%',
      marginLeft: 'auto',
      marginRight: 'auto',
      background: 'white',
      padding: '0 20px'
    } as React.CSSProperties,
    contentContainer: {
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      minHeight: '0%',
      background: '#fff'
    } as React.CSSProperties,
    mainContent: {
      display: 'flex',
      flexGrow: 1,
      minHeight: '0%',
      paddingTop: '20px'
    } as React.CSSProperties,
    leftColumn: {
      flexBasis: '360px',
      flexGrow: 10
    } as React.CSSProperties,
    rightColumn: {
      display: 'flex',
      flexBasis: '360px',
      flexGrow: 10,
      flexDirection: 'column'
    } as React.CSSProperties,
    reserveButton: {
      position: 'relative',
      width: '214px',
      margin: '0 0 10px 10px'
    } as React.CSSProperties,
    reserveButtonArrow: {
      position: 'absolute',
      top: '16px',
      left: '50px',
      width: 0,
      height: 0,
      border: '5px solid transparent',
      borderLeft: '5px solid #fff'
    } as React.CSSProperties,
    requestFormContainer: {
      display: 'flex',
      padding: '10px'
    } as React.CSSProperties,
    requestFormLink: {
      borderBottom: '1px solid',
      cursor: 'pointer'
    } as React.CSSProperties,
    tooltipContainer: {
      position: 'relative'
    } as React.CSSProperties,
    helpButton: {
      background: '#6c757d',
      color: 'white',
      fontSize: '11px',
      lineHeight: '13px',
      width: '15px',
      height: '15px',
      borderRadius: '8px',
      textAlign: 'center' as const,
      cursor: 'help',
      margin: 'auto',
      marginLeft: '2px'
    } as React.CSSProperties,
    tooltip: {
      position: 'absolute',
      top: '110%',
      right: '-60px',
      minWidth: '200px',
      background: '#f8f9fa',
      border: '1px solid #6c757d',
      borderRadius: '3px',
      padding: '5px',
      zIndex: 10,
      color: '#333',
      textAlign: 'left' as const,
      fontWeight: 'normal'
    } as React.CSSProperties
  } as const;
}

export default PcShortDistance;
